import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Phone } from "lucide-react";
import { PHONE, PHONE_HREF } from "../lib/site";
import { useFocusTrap } from "./a11y/useFocusTrap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING = {
  role: "assistant",
  content: "Hi! 👋 I'm the UPV Services assistant. Ask me anything about expedited passports, visas, apostilles, or timelines — or I can help you start a free quote.",
};

const SUGGESTIONS = [
  "How fast can I get a passport?",
  "Do you handle visas for my country?",
  "What documents do I need?",
];

export const ChatWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  // Accessible dialog: trap Tab within the panel, close on Escape, and restore
  // focus to the toggle button when it closes (WCAG 2.4.3 / 2.1.2).
  useFocusTrap(panelRef, open, () => setOpen(false));

  // Move initial focus to the message input when the panel opens.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { document.getElementById("chat-input")?.focus(); }, 80);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const history = messages.filter((m) => m !== GREETING);
    const next = [...messages, { role: "user", content: msg }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { message: msg, history });
      setMessages([...next, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages([...next, { role: "assistant", content: `Sorry, I'm having trouble right now. Please call us at ${PHONE} or use the quote form and a specialist will help you right away.` }]);
    } finally {
      setLoading(false);
    }
  };

  const goQuote = () => {
    setOpen(false);
    navigate("/contact");
    setTimeout(() => document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" }), 400);
  };

  if (location.pathname === "/admin") return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="fixed z-50 right-5 bottom-24 lg:bottom-6 w-14 h-14 rounded-full bg-[#d90429] text-white shadow-[0_8px_30px_rgba(217,4,41,0.4)] flex items-center justify-center hover:bg-[#ef233c] transition-colors"
        aria-label={open ? "Close chat assistant" : "Open chat assistant"}
        aria-expanded={open}
        aria-controls="chat-panel"
        data-testid="chat-toggle"
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} aria-hidden="true">
              <X className="w-6 h-6" />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} aria-hidden="true">
              <MessageCircle className="w-6 h-6" />
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            id="chat-panel"
            ref={panelRef}
            role="dialog"
            aria-modal="false"
            aria-label="UPV chat assistant"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed z-50 right-4 left-4 sm:left-auto bottom-40 lg:bottom-24 sm:w-[380px] max-h-[70vh] bg-white rounded-2xl shadow-[0_20px_60px_rgba(11,25,44,0.28)] border border-black/10 flex flex-col overflow-hidden"
            data-testid="chat-panel"
          >
            <div data-a11y-dark className="bg-[#0b192c] text-white px-5 py-4 flex items-center justify-between relative overflow-hidden">
              <div className="noise-overlay" />
              <div className="relative">
                <h2 className="font-display font-bold text-lg">UPV Assistant</h2>
                <p className="text-xs text-white/70 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-400" aria-hidden="true" /> Typically replies instantly</p>
              </div>
              <a href={PHONE_HREF} className="relative text-white/80 hover:text-white" data-testid="chat-call" aria-label={`Call us at ${PHONE}`}><Phone className="w-5 h-5" aria-hidden="true" /></a>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#f8f9fa]" role="log" aria-live="polite" aria-atomic="false" aria-label="Conversation">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-[14px] leading-relaxed ${m.role === "user" ? "bg-[#d90429] text-white rounded-br-sm" : "bg-white text-[#0a1128] border border-slate-200 rounded-bl-sm"}`} data-testid={`chat-msg-${m.role}`}>
                    <span className="sr-only">{m.role === "user" ? "You said: " : "Assistant said: "}</span>{m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1" aria-label="Assistant is typing">
                    {[0, 1, 2].map((i) => (
                      <span key={i} className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} aria-hidden="true" />
                    ))}
                  </div>
                </div>
              )}
              {messages.length === 1 && !loading && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {SUGGESTIONS.map((s) => (
                    <button type="button" key={s} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full border border-slate-300 bg-white text-[#0b192c] hover:border-[#d90429] hover:text-[#d90429] transition-colors" data-testid="chat-suggestion">
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-slate-200 bg-white p-3">
              <button type="button" onClick={goQuote} className="w-full mb-2.5 rounded-full bg-[#0b192c] text-white py-2.5 text-sm font-semibold hover:bg-[#1a2a40] transition-colors" data-testid="chat-quote-cta">
                Start your order →
              </button>
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2">
                <label htmlFor="chat-input" className="sr-only">Type your question to the assistant</label>
                <input
                  id="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your question…"
                  className="flex-1 rounded-full border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-[#d90429] focus:ring-2 focus:ring-[#d90429]/20 transition-[border-color,box-shadow]"
                  data-testid="chat-input"
                />
                <button type="submit" disabled={loading || !input.trim()} className="shrink-0 w-10 h-10 rounded-full bg-[#d90429] text-white flex items-center justify-center hover:bg-[#ef233c] transition-colors disabled:opacity-50" data-testid="chat-send" aria-label="Send message">
                  <Send className="w-4 h-4" aria-hidden="true" />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
